"use client";

import { motion, AnimatePresence } from "motion/react";
import { Button } from "./ui/button";
import { useState, useRef, useEffect } from "react";

interface PresetQuestionsProps {
  onQuestionSelect: (question: string) => void;
  isVisible: boolean;
  isLoading: boolean;
}

export const PresetQuestions = ({ onQuestionSelect, isVisible, isLoading }: PresetQuestionsProps) => {
  const [showFade, setShowFade] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const initialQuestions = [
    {
      "label": "Impact & Results",
      "fullQuestion": "Can you walk me through a specific project where your technical contributions directly resulted in measurable business impact? I'd love to hear about the problem you solved, your approach, and the quantifiable outcomes."
    },
    {
      "label": "Technical Leadership",
      "fullQuestion": "Tell me about a time when you had to lead a technical decision that involved trade-offs between performance, maintainability, and delivery timeline. How did you evaluate the options and what was the outcome?"
    },
    {
      "label": "Problem Solving",
      "fullQuestion": "Describe a challenging technical problem you encountered that initially seemed impossible to solve. What was your systematic approach to breaking it down, and what creative solution did you implement?"
    },
    {
      "label": "Architecture Design",
      "fullQuestion": "Can you describe a system you architected from scratch? What were the key scalability and reliability considerations, and how did your design decisions prove successful as the system grew?"
    },
    {
      "label": "Cross-Domain Expertise",
      "fullQuestion": "How has your background in financial risk analysis given you a unique advantage in software engineering? Can you share an example where this cross-domain expertise led to a breakthrough solution?"
    },
    {
      "label": "Innovation & Growth",
      "fullQuestion": "What's an example of how you've gone beyond just implementing requirements to innovate and add significant value? How did you identify the opportunity and what was the impact on the team or product?"
    }
  ];

  const [presetQuestions, setPresetQuestions] = useState(initialQuestions);

  const handleQuestionClick = (fullQuestion: string) => {
    // Don't allow clicking if already loading
    if (isLoading) return;
    
    // Move the clicked question to the end
    setPresetQuestions(prevQuestions => {
      const clickedQuestionIndex = prevQuestions.findIndex(q => q.fullQuestion === fullQuestion);
      if (clickedQuestionIndex === -1) return prevQuestions;
      
      const clickedQuestion = prevQuestions[clickedQuestionIndex];
      const remainingQuestions = prevQuestions.filter((_, index) => index !== clickedQuestionIndex);
      
      return [...remainingQuestions, clickedQuestion];
    });
    
    onQuestionSelect(fullQuestion);
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollContainerRef.current) {
        const hasOverflow = scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
        setShowFade(hasOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-xl mx-auto px-4 sm:px-0 relative"
      >
        <div 
          ref={scrollContainerRef}
          className="pl-3 flex gap-3 overflow-x-auto scrollbar-hide pb-2"
        >
          {presetQuestions.map((question, index) => (
            <motion.div
              key={question.fullQuestion}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex-shrink-0"
            >
              <Button
                variant="outline"
                onClick={() => handleQuestionClick(question.fullQuestion)}
                disabled={isLoading}
                className="whitespace-nowrap h-auto py-3 px-4 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {question.label}
              </Button>
            </motion.div>
          ))}
        </div>
        
        {/* Fade effects for overflow */}
        {showFade && (
          <>
            <div className="absolute left-4 sm:left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white dark:from-black to-transparent pointer-events-none" />
            <div className="absolute right-4 sm:right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-black to-transparent pointer-events-none" />
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}; 